import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, LayoutGrid, ExternalLink, Inbox, Users, BarChart3, Coins, Search, FileSignature, TrendingUp, PiggyBank, ShieldAlert, Wand2 } from "lucide-react";
import { useAuth } from "../authApi";
import { LOGO_URL } from "../api";
import { adminApi } from "./adminApi";
import { InstallBackOfficeBanner } from "../InstallApp";

const TABS = [
  { label: "Annonces", to: "/admin", icon: LayoutGrid },
  { label: "Biens proposés", to: "/admin/submissions", icon: Inbox },
  { label: "Affiliations", to: "/admin/affiliations", icon: Users, badgeKey: "pending" },
  { label: "Supervision", to: "/admin/supervision", icon: ShieldAlert, badgeKey: "events" },
  { label: "Acquéreurs", to: "/admin/acquereurs", icon: Search },
  { label: "Offres", to: "/admin/offers", icon: FileSignature },
  { label: "Stats", to: "/admin/stats", icon: BarChart3 },
  { label: "Rentabilité", to: "/admin/roi", icon: TrendingUp },
  { label: "Finances", to: "/admin/finance", icon: PiggyBank },
  { label: "Crédits", to: "/admin/credits", icon: Coins },
  { label: "Agent IA", to: "/admin/agent", icon: Wand2 },
];

export const AdminShell = ({ children, title, action, embedded }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [pending, setPending] = useState(0);
  const [events, setEvents] = useState(0);
  useEffect(() => {
    let alive = true;
    const refresh = () => {
      adminApi.pendingCount().then((d) => { if (alive) setPending(d.pending || 0); }).catch(() => {});
      adminApi.eventsUnread().then((d) => { if (alive) setEvents(d.unread || 0); }).catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 45000);
    return () => { alive = false; clearInterval(id); };
  }, [loc.pathname]);
  const doLogout = async () => { await logout(); nav("/admin/login"); };
  const isActive = (to) => (to === "/admin" ? loc.pathname === "/admin" || loc.pathname.startsWith("/admin/edit") || loc.pathname.startsWith("/admin/new") : loc.pathname.startsWith(to));

  // Mode intégré (coquille unifiée /mon-compte) : uniquement le contenu, sans header/nav BO.
  if (embedded) {
    return (
      <div className="platform-scope text-white" data-testid="admin-embedded">
        {(title || action) && (
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            {title && <h2 className="text-xl font-light text-white flex items-center gap-3"><span className="inline-block w-1.5 h-6 rounded-full bg-[#C19A6B]" />{title}</h2>}
            {action}
          </div>
        )}
        {children}
      </div>
    );
  }

  return (
    <div className="platform-scope min-h-screen bg-[#101216] text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <header className="bg-[#181b21]/95 backdrop-blur-md border-b border-white/10 sticky top-0 z-40 shadow-lg shadow-black/30">
        <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-[#C19A6B] to-transparent" />
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin"><img src={LOGO_URL} alt="OFF MARKETTE" className="w-[140px] h-auto" /></Link>
            <nav className="hidden md:flex items-center gap-0.5">
              {TABS.map((t) => (
                <Link key={t.to} to={t.to} data-testid={`admin-tab-${t.to}`} className={`relative flex items-center gap-1 px-2.5 py-1.5 rounded-[5px] text-[10px] uppercase tracking-wide transition-colors ${isActive(t.to) ? "bg-[#C19A6B] text-white shadow-md shadow-[#C19A6B]/20" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
                  <t.icon className="w-3 h-3" /> {t.label}
                  {t.badgeKey === "pending" && pending > 0 && (
                    <span data-testid="affiliations-badge" className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold">{pending}</span>
                  )}
                  {t.badgeKey === "events" && events > 0 && (
                    <span data-testid="supervision-badge" className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold">{events}</span>
                  )}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2.5 shrink-0">
            <a href="/" target="_blank" rel="noopener noreferrer" className="hidden lg:flex items-center gap-1.5 whitespace-nowrap text-[10px] uppercase tracking-wide text-zinc-400 hover:text-[#C19A6B]"><ExternalLink className="w-3.5 h-3.5" /> Voir le site</a>
            <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-1.5 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-white bg-red-600 hover:bg-red-500 rounded-[5px] px-3 py-2 shadow-md shadow-red-900/30 transition-colors"><LogOut className="w-3.5 h-3.5" /> Déconnexion</button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-10 platform-scope">
        <InstallBackOfficeBanner />
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-light flex items-center gap-3"><span className="inline-block w-1.5 h-7 rounded-full bg-[#C19A6B]" />{title}</h1>
          {action}
        </div>
        {children}
      </main>
    </div>
  );
};
